import { CommandBar, Panel, PanelType, MessageBar, MessageBarType, Selection, Spinner, Stack, PrimaryButton, Toggle, Pivot, PivotItem } from "office-ui-fabric-react";
import { useContext, useEffect, useState } from "react";
import { cancelBooking, cancelBookingSeries, getMyBookings } from "../../../api/bookings";
import { BookingStatus, IMyBooking, SeriesCancellationMode } from "../../../types/Booking.types";
import { ProfileContext } from "../../../Home";
import { DeskBookingForm } from "../forms/DeskBookingForm";
import { RoomBookingForm } from "../forms/RoomBookingForm";
import { BookingList } from "./MyBookingList";

import styles from "./MyBookings.module.scss";

export interface IMyBookingsPanelProps {
    onDismiss: () => void;
}

export const MyBookingsPanel = (props: IMyBookingsPanelProps) => {
    const { onDismiss } = props;
    const userContext = useContext(ProfileContext);
    const standardPivotKey = "standard";
    const recurringPivotKey = "recurring";
    const recurringFeatureEnabled = userContext?.Settings?.RecurringRoomBookingsEnabled === true;

    const [ cancelling, setCancelling ] = useState(false);
    const [ bookings, setBookings ] = useState<IMyBooking[]>();
    const [ showCancelled, setShowCancelled ] = useState(false);
    const [ expandedSeries, setExpandedSeries ] = useState<Record<number, boolean>>({});
    const [ selectedPivot, setSelectedPivot ] = useState(standardPivotKey);
    const [ bookingSelected, setBookingSelected ] = useState<IMyBooking|undefined>();
    const [ viewDeskBooking, setViewDeskBooking ] = useState<IMyBooking|undefined>();
    const [ viewRoomBooking, setViewRoomBooking ] = useState<IMyBooking|undefined>();

    const [ selection ] = useState(new Selection({
        onSelectionChanged: function (this: Selection) {
            const selectedItems = this.getSelection() as IMyBooking[];
            if (selectedItems !== undefined && selectedItems.length > 0) {
                setBookingSelected(selectedItems[0]);
            } else {
                setBookingSelected(undefined);
            }
        }
    }));

    const _load = async () => {
        const response = await getMyBookings();
        if (response.ok) {
            const bookings = await response.json() as IMyBooking[];
            setBookings(bookings);
        }
    }

    const _cancelBooking = async (booking: IMyBooking) => {
        setCancelling(true);
        booking.CancelledByAdmin = false;
        const response = await cancelBooking(booking);
        if (response.ok) {
            setCancelling(false);
            _load();
        } else {
            alert('Unable to cancel booking');
            setCancelling(false);
        }
        selection.setAllSelected(false);
    }

    const _cancelSeriesBooking = async (booking: IMyBooking, mode: SeriesCancellationMode) => {
        if (!booking.RecurringRoomBookingSeriesId) {
            alert("Unable to identify series for this booking");
            return;
        }

        let confirmationText = "";
        if (mode === SeriesCancellationMode.Occurrence) {
            confirmationText = "Cancel only this occurrence?";
        } else if (mode === SeriesCancellationMode.Future) {
            confirmationText = "Cancel this and all future occurrences in the series?";
        } else {
            confirmationText = "Cancel this recurring series from now onward? Past occurrences will remain unchanged.";
        }

        if (!window.confirm(confirmationText)) {
            return;
        }

        setCancelling(true);
        const response = await cancelBookingSeries({
            SeriesId: booking.RecurringRoomBookingSeriesId,
            Mode: mode,
            OccurrenceId: booking.IsSeriesParent === true ? undefined : booking.Id
        });

        if (response.ok) {
            setCancelling(false);
            await _load();
        } else {
            alert('Unable to cancel recurring booking series');
            setCancelling(false);
        }

        selection.setAllSelected(false);
    }

    useEffect(() => {
        _load();
    }, []);

    useEffect(() => {
        if (!recurringFeatureEnabled && selectedPivot === recurringPivotKey) {
            setSelectedPivot(standardPivotKey);
            setBookingSelected(undefined);
            selection.setAllSelected(false);
        }
    }, [recurringFeatureEnabled, selectedPivot, selection]);

    const sortedBookings = (bookings || []).slice().sort((a, b) => new Date(a.Start).getTime() - new Date(b.Start).getTime());

    const visibleBookings = sortedBookings.filter((b) => {
        if (showCancelled) return true;
        return b.Status !== BookingStatus.Rejected && b.Status !== BookingStatus.Cancelled && b.Status !== BookingStatus.AutoCancel;
    });

    const standardBookings = visibleBookings.filter((b) => b.IsSeriesParent !== true && !b.ParentSeriesId);

    const seriesParents = visibleBookings.filter((b) => b.IsSeriesParent === true);
    const seriesChildren = visibleBookings.filter((b) => b.ParentSeriesId && b.IsSeriesParent !== true);

    const displayedSeriesRows = seriesParents.reduce((acc, parent) => {
        acc.push(parent);
        if (parent.RecurringRoomBookingSeriesId && expandedSeries[parent.RecurringRoomBookingSeriesId]) {
            const children = seriesChildren
                .filter((c) => c.ParentSeriesId === parent.RecurringRoomBookingSeriesId)
                .sort((a, b) => new Date(a.Start).getTime() - new Date(b.Start).getTime());
            acc.push(...children);
        }
        return acc;
    }, [] as IMyBooking[]);

    const isRecurringSelection = bookingSelected !== undefined && (bookingSelected.IsSeriesParent === true || Boolean(bookingSelected.ParentSeriesId));
    const isStandardSelection = bookingSelected !== undefined && !isRecurringSelection;
    const isSeriesParentSelection = bookingSelected?.IsSeriesParent === true;
    const isRejectedSelection = bookingSelected?.Status === BookingStatus.Rejected;
    const isCancelledSelection = bookingSelected?.Status === BookingStatus.Cancelled || bookingSelected?.Status === BookingStatus.AutoCancel;
    const canCancelStandard = isStandardSelection && !isRejectedSelection && !isCancelledSelection;
    const canViewOccurrence = isRecurringSelection && !isSeriesParentSelection;
    const canCancelOccurrence = canViewOccurrence && !isRejectedSelection && !isCancelledSelection;
    const canCancelSeries = isRecurringSelection && !isRejectedSelection && !isCancelledSelection;
    const systemAdminUser = userContext?.Profile?.Roles?.some((r) => r.Name === "Admin") === true;

    const standardCommandItems = [
        {
            disabled: !isStandardSelection,
            key: "view",
            text: window.innerWidth < 640 ? "View" : "View Booking",
            onClick: () => {
                if (bookingSelected) {
                    if (bookingSelected.DeskId) {
                        setViewDeskBooking(bookingSelected);
                    } else {
                        setViewRoomBooking(bookingSelected);
                    }
                }
            }
        },
        {
            disabled: !canCancelStandard || cancelling,
            key: "cancel",
            text: window.innerWidth < 640 ? "Cancel" : "Cancel Booking",
            onRender: cancelling ? () => <Spinner label="Cancelling..." /> : undefined,
            onClick: () => {
                if (bookingSelected) {
                    _cancelBooking(bookingSelected);
                }
            }
        }
    ];

    const recurringCommandItems = [
        {
            disabled: !canViewOccurrence || cancelling,
            key: "viewOccurrence",
            text: window.innerWidth < 640 ? "View" : "View Occurrence",
            onClick: () => {
                if (bookingSelected && bookingSelected.IsSeriesParent !== true) {
                    setViewRoomBooking(bookingSelected);
                }
            }
        },
        {
            disabled: !canCancelOccurrence || cancelling,
            key: "cancelOccurrence",
            text: window.innerWidth < 640 ? "Cancel Occ" : "Cancel Occurrence",
            onClick: () => {
                if (bookingSelected) {
                    _cancelSeriesBooking(bookingSelected, SeriesCancellationMode.Occurrence);
                }
            }
        },
        {
            disabled: !canCancelSeries || cancelling,
            key: "cancelSeries",
            text: window.innerWidth < 640 ? "Cancel Series" : "Cancel Series",
            onClick: () => {
                if (bookingSelected) {
                    _cancelSeriesBooking(bookingSelected, SeriesCancellationMode.Series);
                }
            }
        }
    ];

    return (
        <Panel type={PanelType.custom} customWidth="900px" isOpen={true} headerText="My Bookings" onDismiss={onDismiss}>
            <p>Bookings that you make - for you and on behalf of others - will appear here:</p>
            { window.innerWidth < 640 && (
                <MessageBar messageBarType={MessageBarType.info}>You can scroll the table below with the mouse or your finger</MessageBar>
            )}
            <CommandBar
                className={styles.commandBar}
                items={recurringFeatureEnabled && selectedPivot === recurringPivotKey ? recurringCommandItems : standardCommandItems}
            />
            <Toggle
                label={showCancelled ? "Hide Cancelled / Rejected" : "Show Cancelled / Rejected"}
                checked={showCancelled}
                onText="Shown"
                offText="Hidden"
                onChange={(e, checked) => setShowCancelled(checked === true)}
            />
            { bookings !== undefined && bookings.length > 0 && (
                <>
                    <Pivot
                        selectedKey={selectedPivot}
                        onLinkClick={(item) => {
                            const nextKey = item?.props.itemKey ?? standardPivotKey;
                            setSelectedPivot(nextKey);
                            setBookingSelected(undefined);
                            selection.setAllSelected(false);
                        }}
                    >
                        <PivotItem headerText="Standard Bookings" itemKey={standardPivotKey}>
                            <BookingList
                                label="Standard Bookings"
                                listKey="standard"
                                selection={selection}
                                bookings={standardBookings}
                                showStatus={true}
                            />
                        </PivotItem>
                        { recurringFeatureEnabled && systemAdminUser && (
                            <PivotItem headerText="Recurring Bookings" itemKey={recurringPivotKey}>
                                <BookingList
                                    label="Recurring Room Booking Series"
                                    listKey="recurring"
                                    selection={selection}
                                    bookings={displayedSeriesRows}
                                    onToggleSeries={(seriesId: number) => {
                                        setExpandedSeries((curr) => ({
                                            ...curr,
                                            [seriesId]: !curr[seriesId]
                                        }));
                                    }}
                                    expandedSeries={expandedSeries}
                                    isSeriesView={true}
                                    tooltip="Series rows can be expanded to view individual occurrences"
                                    showStatus={true}
                                />
                            </PivotItem>
                        )}
                    </Pivot>
                    <Stack horizontal horizontalAlign="end" style={{marginTop: 20}}>
                        <PrimaryButton text="Export" iconProps={{iconName: "Export"}}
                            href={"/api/bookings/my/export"} target="_blank" />
                    </Stack>
                </>
            )}
            { viewDeskBooking !== undefined && (
                <DeskBookingForm startDateStr={viewDeskBooking.Start} endDateStr={viewDeskBooking.Finish}
                    floorplanId={viewDeskBooking.FloorplanId} locationId={viewDeskBooking.LayoutId}
                    onDismiss={() => setViewDeskBooking(undefined)}
                    booking={viewDeskBooking}
                    readOnly={true}
                    postCode={viewDeskBooking.PostCode}
                    />
            )}
            { viewRoomBooking !== undefined && (
                <RoomBookingForm
                    finish={new Date(viewRoomBooking.Finish)}
                    onDismiss={(changesMade) => {
                        setViewRoomBooking(undefined);
                        if (changesMade) {
                            _load();
                        }
                    }}
                    booking={viewRoomBooking}
                    roomId={viewRoomBooking.RoomId as number}
                    start={new Date(viewRoomBooking.Start)}
                />
            )}
        </Panel>    
    );
}